import React from 'react';
import I18n from 'i18n-js';
import classNames from 'classnames';
import ConditionDisplay from '@reverbdotcom/commons/src/components/condition_display';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { listingItemPagePath } from '@reverbdotcom/commons/src/url_helpers';
import { ImageCarousel } from '@reverbdotcom/commons';
import Item2Pricing from './item2_pricing';
import {
  BumpKeyFragment,
  FeaturedListingCSPFragment,
  RefreshedFeaturedListingModuleDataFragment,
  arbiter_Interaction_InteractionType,
} from '@reverbdotcom/commons/src/gql/graphql';
import { useBumpInteractionTracking } from '@reverbdotcom/commons/src/trackBumpInteraction';
import { buildListingClickEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { RefreshedFeaturedListingShopInfo } from './refreshed_featured_listing_shop_info';
import { gql } from '@reverbdotcom/commons/src/gql';
import { isDigital } from '@reverbdotcom/commons/src/listing_helpers';
import { RCButton, RCTag } from '@reverbdotcom/cadence/components';
import { WatchBadge, DisplayStyle } from '@reverbdotcom/commons/src/components/watch_badge';
import BestSellerBadge from './best_seller_badge';
import { useEppoAssignment } from '@reverbdotcom/commons/src/user_hooks';
import eppoFlags from '@reverbdotcom/commons/src/eppoFlags';

export const RefreshedFeaturedListingModuleFragment = gql(`
  fragment RefreshedFeaturedListingModuleData on Listing {
    _id
    title
    slug
    description
    condition {
      displayName
      conditionSlug
    }
    ...ItemImageFields
    ...RefreshedFeaturedListingShopInfoData
    ...Item2PricingData
    ...SignalsData
    ...Item2ReturnPolicyData
  }
`);

interface Props {
  listing: RefreshedFeaturedListingModuleDataFragment & BumpKeyFragment;
  isBumped: boolean;
  isLoading: boolean;
  trackName: string;
  csp: FeaturedListingCSPFragment;
}

export function RefreshedFeaturedListingModule({
  listing,
  isBumped,
  isLoading,
  trackName,
  csp,
}: Props) {
  const rolledUpFeaturedListingExpAssignment = useEppoAssignment({ flagKey: eppoFlags.ROLLED_UP_FEATURED_LISTING });
  const isRolledUpFeaturedListingExpEnabled = rolledUpFeaturedListingExpAssignment !== 'control';

  const bumpInteractionTracking = useBumpInteractionTracking({
    interactionType: arbiter_Interaction_InteractionType.CLICK,
    bumpKey: listing?.bumpKey,
    listingId: listing?.id,
  });

  const classes = classNames(
    'refreshed-featured-listing-module',
    { 'refreshed-featured-listing-module--loading': !listing && isLoading },
    { 'refreshed-featured-listing-module--reloading': listing && isLoading },
  );

  if (!listing && isLoading) {
    return (
      <article className={classes}>
        <div className="refreshed-featured-listing-module__image"></div>
      </article>
    );
  }

  if (!listing) { return null; }

  const { condition, title, description } = listing;

  return (
    <>
      <h3 className="refreshed-featured-listing-module__section-heading">
        {I18n.t('discovery.csp2.featuredListing')}
      </h3>
      <article className={classes}>
        <div className="refreshed-featured-listing-module__image">
          <WatchBadge
            listing={listing}
            displayStyle={DisplayStyle.RC_FAVORITE_BUTTON}
            parentComponentName={trackName}
            useToast
          />
          <ImageCarousel
            images={listing.largeImages.map((img) => img.source)}
            bumpInteractionTracking={bumpInteractionTracking}
            parentComponentName={trackName}
          />
        </div>
        <div className="refreshed-featured-listing-module__header">
          <CoreLink
            className="refreshed-featured-listing-module__header-title"
            to={listingItemPagePath(listing)}
            clickEvent={buildListingClickEvent({
              listing,
              componentName: trackName,
              displayStyle: 'title link',
            })}
          >
            {title}
          </CoreLink>
          <div className="refreshed-featured-listing-module__header-tags">
            <RCTag>
              <ConditionDisplay
                slug={condition?.conditionSlug}
                displayName={condition?.displayName}
                isDigitalListing={isDigital(listing)}
                showTooltip
                textOnly
              />
            </RCTag>
            {isBumped && (
              <RCTag>{I18n.t('commons.bumpsView.header')}</RCTag>
            )}
            <RCTag variant="highlight">
              <BestSellerBadge
                cspId={csp?.id}
                categories={csp?.categories}
                listingId={listing.id}
              />
            </RCTag>
          </div>
        </div>
        <div className="refreshed-featured-listing-module__shop">
          <RefreshedFeaturedListingShopInfo listing={listing} />
        </div>
        <div className="refreshed-featured-listing-module__about">
          <h3 className="refreshed-featured-listing-module__about-header">
            {I18n.t('discovery.item2Description.about')}
          </h3>
          <div className="refreshed-featured-listing-module__about-content">
            <SanitizedRender
              html={description}
              truncationLimit={200}
              inlineTagsOnly
            />
          </div>
          <CoreLink
            className="refreshed-featured-listing-module__about-link"
            to={listingItemPagePath(listing)}
            clickEvent={buildListingClickEvent({
              listing,
              componentName: trackName,
              displayStyle: 'secondary link',
            })}
          >
            <RCButton size="mini">
              {I18n.t('discovery.featuredListingModule.viewListing')}
            </RCButton>
          </CoreLink>
        </div>
        <div className="refreshed-featured-listing-module__pricing">
          <Item2Pricing
            listing={listing}
            negotiations={[]}
            parentComponentName={trackName}
            isRolledUpFeaturedListingExpEnabled={isRolledUpFeaturedListingExpEnabled}
            onFeaturedListing
          />
        </div>
      </article>
    </>
  );
}

export default RefreshedFeaturedListingModule;
